import * as React from 'react';
import { Text, ListItem, UnorderedList } from '@chakra-ui/react';
import {
  UploadActions,
  UploadButtonSection,
  Modal,
  UploadComplete as UploadCompleteSection,
  AcknowledgeBox,
  RequirementsSummary
} from 'shared-components';
import { EmploymentStatus } from '../../../../../store/student/financeDocuments/types';

interface IProps {
  sectionTitle: string;
  fileNames: string[];
  complete: boolean;
  onClose: () => void;
  onContinue: () => void;
  onUpload: () => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  leadSponsor: string;
  onAcknowledgeToggle: (status: string) => void;
  onSaveMetaDataForNoDoc: (metadata?: any) => void;
}

const CurriculumVitaeModal: React.FC<IProps> = (props: IProps) => {
  const { complete, infoOnly, docStatus, docKey, sectionTitle, leadSponsor, fileNames } = props;

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  const onContinue = () => {
    props.onContinue();
    props.onClose();
  };

  return (
    <Modal id={'curriculumVitae'} show={true} title={sectionTitle} onClose={handleOnClose} hideFooter>
      {infoOnly ? (
        <>
          <Text>
            During your visa application you may be required to provide a recent CV or resume to verify your{' '}
            {leadSponsor !== 'student' ? `${leadSponsor}'s` : ''} employment or professional history.
          </Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            label="Return to document upload"
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
          />
        </>
      ) : (
        <>
          {complete ? (
            <UploadCompleteSection fileNames={fileNames} onUpload={() => props.onUpload()} onContinue={onContinue} />
          ) : (
            <>
              <Text>
                We require a recent CV or resume to verify your {leadSponsor !== 'student' ? `${leadSponsor}'s` : ''}{' '}
                employment or professional history.
              </Text>

              <RequirementsSummary title="Please ensure the document:">
                <UnorderedList>
                  <ListItem>Covers the past 5 years of employment</ListItem>
                  <ListItem>
                    Matches your {leadSponsor !== 'student' ? `${leadSponsor}'s` : ''} name as per your supplied finance
                    documents
                  </ListItem>
                </UnorderedList>
              </RequirementsSummary>

              <UploadButtonSection onUpload={props.onUpload}>
                Awaiting upload of <b>CV / Resume Document</b>
              </UploadButtonSection>

              <UploadActions complete={false} onContinue={onContinue} />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default CurriculumVitaeModal;
